import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { AppLayout } from '@/layouts/app-layout'
import { LoginPage } from '@/pages/login-page'
import { DashboardPage } from '@/pages/dashboard-page'
import { UsersPage } from '@/pages/users-page'
import { SettingsPage } from '@/pages/settings-page'
import { UserProfilePage } from '@/pages/user-profile-page'
import { PricingItemsPage } from '@/pages/pricing-items-page'
import { ExclusionsPage } from '@/pages/exclusions-page'
import { CrewsPage } from '@/pages/crews-page'
import { EstimatesListPage } from '@/pages/estimates-list-page'
import { EstimateBuilderPage } from '@/pages/estimate-builder-page'
import { ProposalsListPage } from '@/pages/proposals-list-page'
import { ProposalPreviewPage } from '@/pages/proposal-preview-page'
import { ProposalSignaturePage } from '@/pages/proposal-signature-page'
import { ReportsHubPage } from '@/pages/reports-hub-page'
import { ReportDetailPage } from '@/pages/report-detail-page'
import { RegisterPage } from '@/pages/register-page'
import { ForgotPasswordPage } from '@/pages/forgot-password-page'
import { ResetPasswordPage } from '@/pages/reset-password-page'
import { CustomersListPage } from '@/pages/customers-list-page'
import { CustomerDetailPage } from '@/pages/customer-detail-page'
import { CustomerImportPage } from '@/pages/customer-import-page'
import { HelpPage } from '@/pages/help-page'
import { NotFoundPage } from '@/pages/not-found-page'
import { OpportunitiesListPage } from '@/pages/opportunities-list-page'
import { OpportunityDetailPage } from '@/pages/opportunity-detail-page'
import { OpportunityCreatePage } from '@/pages/opportunity-create-page'
import { PricingHaulDumpPage } from '@/pages/pricing-haul-dump-page'
import { PricingDefaultsPage } from '@/pages/pricing-defaults-page'
import { PricingImportPage } from '@/pages/pricing-import-page'
import { InvoicesListPage } from '@/pages/invoices-list-page'
import { InvoiceDetailPage } from '@/pages/invoice-detail-page'
import { PublicInvoicePage } from '@/pages/public-invoice-page'
import { ImpersonatePage } from '@/pages/impersonate-page'
import { AutoLoginPage } from '@/pages/auto-login-page'
import { UserSettingsPage } from '@/pages/user-settings-page'
import { PrivacyPolicyPage } from '@/pages/privacy-policy-page'
import { TermsPage } from '@/pages/terms-page'

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate)

  useEffect(() => {
    hydrate()
  }, [hydrate])

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/proposals/public/:shareableLink" element={<ProposalSignaturePage />} />
        <Route path="/invoices/public/:shareableLink" element={<PublicInvoicePage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="/welcome" element={<AutoLoginPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Protected app routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Estimates */}
          <Route path="/estimates" element={<EstimatesListPage />} />
          <Route path="/estimates/:id" element={<EstimateBuilderPage />} />

          {/* Proposals */}
          <Route path="/proposals" element={<ProposalsListPage />} />
          <Route path="/proposals/preview" element={<ProposalPreviewPage />} />
          <Route path="/proposals/:id" element={<ProposalPreviewPage />} />

          {/* Invoices */}
          <Route path="/invoices" element={<InvoicesListPage />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />

          {/* Reports (not visible to estimators) */}
          <Route path="/reports" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN', 'MANAGER']}><ReportsHubPage /></ProtectedRoute>} />
          <Route path="/reports/:reportId" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN', 'MANAGER']}><ReportDetailPage /></ProtectedRoute>} />

          {/* Opportunities */}
          <Route path="/opportunities" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN', 'MANAGER', 'ESTIMATOR']}><OpportunitiesListPage /></ProtectedRoute>} />
          <Route path="/opportunities/new" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN', 'MANAGER']}><OpportunityCreatePage /></ProtectedRoute>} />
          <Route path="/opportunities/:id" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN', 'MANAGER', 'ESTIMATOR']}><OpportunityDetailPage /></ProtectedRoute>} />

          {/* RFP Requests — retired, redirect to Opportunities */}
          <Route path="/rfp-requests" element={<Navigate to="/opportunities" replace />} />
          <Route path="/rfp-requests/new" element={<Navigate to="/opportunities/new" replace />} />
          <Route path="/rfp-requests/:id" element={<Navigate to="/opportunities" replace />} />
          <Route path="/rfp-requests/:id/edit" element={<Navigate to="/opportunities" replace />} />

          {/* Customers */}
          <Route path="/customers" element={<CustomersListPage />} />
          <Route path="/customers/import" element={<CustomerImportPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />

          {/* Help & FAQ */}
          <Route path="/help" element={<HelpPage />} />

          {/* Pricing — admin only */}
          <Route path="/pricing/equipment" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingItemsPage key="equipment" category="equipment" title="Equipment" addLabel="Equipment" /></ProtectedRoute>} />
          <Route path="/pricing/laborers" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingItemsPage key="labor" category="labor" title="Laborers" addLabel="Laborer" /></ProtectedRoute>} />
          <Route path="/pricing/miscellaneous" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingItemsPage key="miscellaneous" category="miscellaneous" title="Miscellaneous" addLabel="Miscellaneous Item" /></ProtectedRoute>} />
          <Route path="/pricing/materials" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingItemsPage key="materials" category="materials" title="Materials" addLabel="Material" /></ProtectedRoute>} />
          <Route path="/pricing/striping" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingItemsPage key="striping" category="striping" title="Striping" addLabel="Striping Item" /></ProtectedRoute>} />
          <Route path="/pricing/exclusions" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><ExclusionsPage /></ProtectedRoute>} />
          <Route path="/pricing/haul-dump" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingHaulDumpPage /></ProtectedRoute>} />
          <Route path="/pricing/defaults" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingDefaultsPage /></ProtectedRoute>} />
          <Route path="/pricing/import" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><PricingImportPage /></ProtectedRoute>} />
          <Route path="/pricing/crews" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><CrewsPage /></ProtectedRoute>} />

          {/* Settings — admin only (profile available to all) */}
          <Route path="/settings/profile" element={<UserProfilePage />} />
          <Route path="/settings/notifications" element={<UserSettingsPage />} />
          <Route path="/settings/company" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/users" element={<ProtectedRoute roles={['SUPERADMIN', 'ADMIN']}><UsersPage /></ProtectedRoute>} />
        </Route>

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  )
}
